import {apiClient, queryClient} from '@app/ui/http/query-client';
import {BootstrapData} from './bootstrap-data';
import {useQuery} from '@tanstack/react-query';

declare global {
  interface Window {
    bootstrapData?: string | BootstrapData;
  }
}

const queryKey = ['bootstrapData'];

export function getBootstrapData(): BootstrapData {
  return queryClient.getQueryData(queryKey)!;
}

export function invalidateBootstrapData() {
  queryClient.invalidateQueries({
    queryKey: ['bootstrapData'],
  });
}

export function setBootstrapData(data: string | BootstrapData) {
  queryClient.setQueryData<BootstrapData>(
    queryKey,
    typeof data === 'string' ? decodeBootstrapData(data) : data,
  );
}

export function mergeBootstrapData(partialData: Partial<BootstrapData>) {
  setBootstrapData({
    ...getBootstrapData(),
    ...partialData,
  });
}

// Default bootstrap data to prevent null errors
const defaultBootstrapData: BootstrapData = {
  themes: { all: [], selectedThemeId: null },
  sentry_release: '',
  i18n: {
    lines: {},
    id: 'en',
    name: 'English',
    model_type: 'localization',
  },
  settings: {
    branding: {
      site_name: 'G2G Vault',
      site_description: '',
      logo_light: '',
      logo_dark: '',
    },
    base_url: typeof window !== 'undefined' ? window.location.origin : '',
    themes: {
      user_change: true,
      default_id: 0,
    },
    version: '1.0.0',
    html_base_uri: '',
    dates: {
      format: 'YYYY-MM-DD',
      default_timezone: 'UTC',
    },
    i18n: {
      enable: false,
      default_localization: 'en',
    },
    billing: {
      enable: false,
    },
    notifications: {
      integrated: false,
    },
    workspaces: {
      integrated: false,
    },
  } as any,
  user: null,
  guest_role: null,
  default_meta_tags: [],
  show_cookie_notice: false,
};

// set bootstrap data that was provided with initial request from backend
const initialBootstrapData = (
  typeof window !== 'undefined' && window.bootstrapData
    ? decodeBootstrapData(window.bootstrapData)
    : defaultBootstrapData
) as BootstrapData;

// make sure initial data is available right away when accessing it via "getBootstrapData()"
queryClient.setQueryData(queryKey, initialBootstrapData);

export function useBackendBootstrapData() {
  return useQuery<BootstrapData>({
    queryKey,
    queryFn: fetchBootstrapData,
    staleTime: Infinity,
    initialData: initialBootstrapData,
  });
}

const fetchBootstrapData = async (): Promise<BootstrapData> => {
  // Return a default bootstrap data if fetching fails
  try {
    const response = await apiClient.get('http://bedesk.test/api/v1/bootstrap-data');
    return decodeBootstrapData(response.data.data);
  } catch (error) {
    console.warn('Failed to fetch bootstrap data, using defaults:', error);
    // Return minimal default bootstrap data with required settings
    return {
      themes: { all: [], selectedThemeId: null },
      sentry_release: '',
      i18n: {
        lines: {},
        id: 'en',
        name: 'English',
        model_type: 'localization',
      },
      settings: {
        branding: {
          site_name: 'G2G Vault',
          site_description: '',
          logo_light: '',
          logo_dark: '',
        },
        base_url: window.location.origin,
        themes: {
          user_change: true,
          default_id: 0,
        },
        version: '1.0.0',
        html_base_uri: '',
        dates: {
          format: 'YYYY-MM-DD',
          default_timezone: 'UTC',
        },
        i18n: {
          enable: false,
          default_localization: 'en',
        },
        billing: {
          enable: false,
        },
        notifications: {
          integrated: false,
        },
        workspaces: {
          integrated: false,
        },
      } as any,
      user: null,
      guest_role: null,
      default_meta_tags: [],
      show_cookie_notice: false,
    };
  }
};

function decodeBootstrapData(data: string | BootstrapData): BootstrapData {
  return typeof data === 'string' ? JSON.parse(data) : data;
}
